import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import {
  Text,
  Box,
  Button,
  useDisclosure,
  Flex,
  Switch,
  Skeleton,
  IconButton,
  useClipboard,
} from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import CohortPatientsForm from "./CohortPatientsForm";
import { z } from "zod";
import {
  adtSchema,
  booleanStringSchema,
  Cohort,
  COHORT_COLORS,
  COHORT_FREQUENCIES,
  CohortColor,
  cohortBaseFormSchema,
  Frequency,
  notificationSchema,
  notificationScheduleSchema,
  scheduleFormSchema,
} from "../../../api/schemas/cohort";
import { createCohort, getCohortOverrides, updateCohort } from "../../../api/internal";
import DrawerForm from "../../shared/form/Drawer";
import { Input } from "../../shared/form/Input";
import { Select } from "../../shared/form/Select";
import useMetriportToast from "../../shared/toast";
import { captureAndDisplayError } from "../../shared/util";

const formSchema = cohortBaseFormSchema.omit({ overrides: true }).extend({
  adt: adtSchema.extend({ enabled: booleanStringSchema }),
  hie: scheduleFormSchema,
  pharmacy: notificationScheduleSchema
    .extend({ notifications: booleanStringSchema })
    .extend({ schedule: scheduleFormSchema }),
  laboratory: notificationSchema.extend({ notifications: booleanStringSchema }),
});
type FormData = z.infer<typeof formSchema>;

const colorOptions = COHORT_COLORS.map(color => ({ value: color, label: color }));
const booleanOptions = [
  { value: "true", label: "Enabled" },
  { value: "false", label: "Disabled" },
];
const frequencyOptions = COHORT_FREQUENCIES.map(freq => ({
  value: freq,
  label: freq.charAt(0).toUpperCase() + freq.slice(1),
}));

const frequencyHints: Record<string, string> = {
  weekly: "Runs every Saturday",
  biweekly: "Runs every other Saturday",
  monthly: "Runs on the first Saturday of each month",
};

function MonitoringCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} mt={3}>
      <Text fontWeight="semibold" fontSize="sm">
        {title}
      </Text>
      {subtitle && (
        <Text fontSize="xs" color="gray.500">
          {subtitle}
        </Text>
      )}
      <Box mt={2}>{children}</Box>
    </Box>
  );
}

function FrequencyHint({ frequency }: { frequency: string }) {
  const hint = frequencyHints[frequency];
  if (!hint) return null;
  return (
    <Text fontSize="xs" color="gray.400" mt={1}>
      {hint}
    </Text>
  );
}

export default function CohortForm({
  isOpen,
  onClose,
  onSubmit,
  cxId,
  cohort,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (cohort: Cohort) => void;
  cxId: string;
  cohort?: Cohort;
}) {
  const isEdit = !!cohort;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [overrideValues, setOverrideValues] = useState<Record<string, boolean>>({});
  const {
    isOpen: isOpenUpdatePatients,
    onOpen: onOpenUpdatePatients,
    onClose: onCloseUpdatePatients,
  } = useDisclosure();
  const toast = useMetriportToast();
  const { onCopy, hasCopied } = useClipboard(cohort?.id ?? "");

  useEffect(() => {
    if (hasCopied) toast.success({ title: "Copied." });
  }, [hasCopied]);

  const cohortId = cohort?.id;
  useEffect(() => {
    if (!isOpen || !cohortId) return;
    setLoadingOverrides(true);
    getCohortOverrides(cxId, cohortId)
      .then(overrides => {
        setOverrideValues(overrides);
      })
      .catch(error => {
        console.error("Failed to fetch cohort overrides", error);
        toast.error({ title: "Failed to load overrides" });
      })
      .finally(() => setLoadingOverrides(false));
  }, [isOpen, cxId, cohortId]);

  const defaultValues = useMemo((): FormData => {
    if (!cohort) {
      return {
        name: "",
        description: "",
        color: "white",
        adt: { enabled: "false" },
        hie: { enabled: "false", frequency: "monthly" },
        pharmacy: {
          notifications: "false",
          schedule: { enabled: "false", frequency: "monthly" },
        },
        laboratory: { notifications: "false" },
      };
    }
    return {
      name: cohort.name,
      description: cohort.description ?? "",
      color: cohort.color,
      adt: { enabled: `${cohort.settings.monitoring.adt.enabled}` },
      hie: {
        enabled: `${cohort.settings.monitoring.hie.enabled}`,
        frequency: cohort.settings.monitoring.hie.frequency,
      },
      pharmacy: {
        notifications: `${cohort.settings.monitoring.pharmacy.notifications}`,
        schedule: {
          enabled: `${cohort.settings.monitoring.pharmacy.schedule.enabled}`,
          frequency: cohort.settings.monitoring.pharmacy.schedule.frequency,
        },
      },
      laboratory: {
        notifications: `${cohort.settings.monitoring.laboratory.notifications}`,
      },
    };
  }, [cohort]);

  const handleSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);

      const cohortData = {
        name: data.name,
        description: data.description,
        color: data.color as CohortColor,
        settings: {
          monitoring: {
            adt: { enabled: data.adt.enabled === "true" },
            hie: {
              enabled: data.hie.enabled === "true",
              frequency: data.hie.frequency as Frequency,
            },
            pharmacy: {
              notifications: data.pharmacy.notifications === "true",
              schedule: {
                enabled: data.pharmacy.schedule.enabled === "true",
                frequency: data.pharmacy.schedule.frequency as Frequency,
              },
            },
            laboratory: { notifications: data.laboratory.notifications === "true" },
          },
          overrides: overrideValues,
        },
      };

      const result = isEdit
        ? await updateCohort(cxId, cohort.id, cohortData)
        : await createCohort(cxId, cohortData);

      toast.success({ title: isEdit ? "Cohort updated." : "Cohort created." });
      onSubmit(result);
    } catch (error) {
      const axiosError = error as {
        response?: {
          data?: {
            detail?: string;
            additionalInfo?: {
              invalidOverrides?: string;
              validOverrides?: string;
            };
          };
        };
      };
      const errorData = axiosError?.response?.data;
      const errorMsg = errorData?.detail ?? `Failed to ${isEdit ? "update" : "create"} cohort`;
      const invalidOverrides = errorData?.additionalInfo?.invalidOverrides;
      const validOverrides = errorData?.additionalInfo?.validOverrides;

      let description = "";
      if (invalidOverrides) {
        description += `Invalid: ${invalidOverrides}`;
      }
      if (validOverrides) {
        description += `\nValid options: ${validOverrides}`;
      }

      toast.error({
        title: errorMsg,
        description: description || undefined,
        duration: 10000,
      });
      captureAndDisplayError({
        error,
        msg: errorMsg,
        context: isEdit ? "update" : "create",
        captureMsg: `Component: CohortForm - ${isEdit ? "update" : "create"} failed`,
        toast,
        extras: { cxId, cohortId: cohort?.id },
        hideDisplay: true,
      });
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <DrawerForm<FormData>
        title={isEdit ? `Edit Cohort: ${cohort.name}` : "Create Cohort"}
        isOpen={isOpen}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={onClose}
        resolver={zodResolver(formSchema)}
        defaultValues={defaultValues}
        btSaveId="cohort-save"
        btCloseId="cohort-close"
      >
        {({ register, formState: { errors }, watch }) => (
          <>
            {isEdit && (
              <Flex align="center" mb={4}>
                <Text fontSize="sm" color="gray.600">
                  ID: {cohort.id}
                </Text>
                <IconButton
                  aria-label="Copy cohort ID"
                  icon={<CopyIcon />}
                  size="xs"
                  variant="ghost"
                  ml={2}
                  onClick={onCopy}
                />
              </Flex>
            )}
            <Input {...register("name")} label="Name" error={get(errors, "name")} />
            <Box mt={4}>
              <Input
                {...register("description")}
                label="Description"
                error={get(errors, "description")}
              />
            </Box>
            <Box mt={4}>
              <Select
                {...register("color")}
                label="Color"
                options={colorOptions}
                error={get(errors, "color")}
              />
            </Box>

            <Text mt={6} fontWeight="bold">
              Monitoring Settings
            </Text>

            <MonitoringCard
              title="ADT"
              subtitle="Hospital admission, discharge, and transfer alerts"
            >
              <Select
                {...register("adt.enabled")}
                label="ADT Alerts"
                options={booleanOptions}
                error={get(errors, "adt.enabled")}
              />
            </MonitoringCard>

            <MonitoringCard
              title="HIE"
              subtitle="Medical record pulls from health information exchanges"
            >
              <Select
                {...register("hie.enabled")}
                label="HIE Pulls"
                options={booleanOptions}
                error={get(errors, "hie.enabled")}
              />
              <Box
                opacity={watch("hie.enabled") === "true" ? 1 : 0.4}
                pointerEvents={watch("hie.enabled") === "true" ? "auto" : "none"}
                mt={2}
              >
                <Select
                  {...register("hie.frequency")}
                  label="Pull Frequency"
                  options={frequencyOptions}
                  error={get(errors, "hie.frequency")}
                  isDisabled={watch("hie.enabled") !== "true"}
                />
                <FrequencyHint frequency={watch("hie.frequency")} />
              </Box>
            </MonitoringCard>

            <MonitoringCard
              title="Pharmacy"
              subtitle="Prescription data via Surescripts — notifications and scheduled pulls are independent"
            >
              <Box mb={3}>
                <Select
                  {...register("pharmacy.notifications")}
                  label="Real-Time Notifications"
                  options={booleanOptions}
                  error={get(errors, "pharmacy.notifications")}
                />
              </Box>
              <Select
                {...register("pharmacy.schedule.enabled")}
                label="Scheduled History Pull"
                options={booleanOptions}
                error={get(errors, "pharmacy.schedule.enabled")}
              />
              <Box
                opacity={watch("pharmacy.schedule.enabled") === "true" ? 1 : 0.4}
                pointerEvents={watch("pharmacy.schedule.enabled") === "true" ? "auto" : "none"}
                mt={2}
              >
                <Select
                  {...register("pharmacy.schedule.frequency")}
                  label="Pull Frequency"
                  options={frequencyOptions}
                  error={get(errors, "pharmacy.schedule.frequency")}
                  isDisabled={watch("pharmacy.schedule.enabled") !== "true"}
                />
                <FrequencyHint frequency={watch("pharmacy.schedule.frequency")} />
              </Box>
            </MonitoringCard>

            <MonitoringCard title="Laboratory" subtitle="Lab results from Quest Diagnostics">
              <Select
                {...register("laboratory.notifications")}
                label="Lab Result Notifications"
                options={booleanOptions}
                error={get(errors, "laboratory.notifications")}
              />
              <Text fontSize="xs" color="gray.400" mt={2}>
                Subscriptions are created weekly. Once active, results are received as they become
                available.
              </Text>
            </MonitoringCard>

            {isEdit && watch("adt.enabled") === "true" && (
              <>
                <Text mt={6} fontWeight="bold">
                  Overrides
                </Text>
                <Box mt={2}>
                  {loadingOverrides ? (
                    <Skeleton height="40px" />
                  ) : Object.keys(overrideValues).length === 0 ? (
                    <Text color="gray.500" fontSize="sm">
                      No overrides available
                    </Text>
                  ) : (
                    <Flex direction="column" gap={2}>
                      {Object.entries(overrideValues).map(([key, value]) => (
                        <Flex
                          key={key}
                          align="center"
                          justify="space-between"
                          p={2}
                          borderRadius="md"
                          bg={value ? "green.50" : "gray.50"}
                          border="1px solid"
                          borderColor={value ? "green.200" : "gray.200"}
                        >
                          <Text fontSize="sm" fontWeight={value ? "medium" : "normal"}>
                            {key}
                          </Text>
                          <Switch
                            colorScheme="green"
                            isChecked={value}
                            onChange={e =>
                              setOverrideValues(prev => ({
                                ...prev,
                                [key]: e.target.checked,
                              }))
                            }
                          />
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Box>
              </>
            )}

            {isEdit && (
              <>
                <Text mt={6} fontWeight="bold">
                  Patients
                </Text>
                <Box mt={2}>
                  <Button colorScheme="blue" size="sm" onClick={onOpenUpdatePatients}>
                    Update Patients
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DrawerForm>
      {isEdit && cohort && (
        <CohortPatientsForm
          isOpen={isOpenUpdatePatients}
          onClose={onCloseUpdatePatients}
          onSubmit={onSubmit}
          cxId={cxId}
          cohortId={cohort.id}
          cohortName={cohort.name}
        />
      )}
    </>
  );
}
